// Drivers service - moved from database.js
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  serverTimestamp, 
  db 
} from '../../utils/database/firebase-config';
import cache from '../../utils/dataCache';
import { auditService, AUDIT_MODULES, AUDIT_ACTIONS, AUDIT_TAGS } from '../system/audit';

const driverLabel = (d) => d?.name || d?.driverCode || d?.driverNumber || d?.id || 'driver';

export const driversService = {
  /**
   * Generate driver code
   */
  async generateDriverCode(companyId) {
    try {
      const driversRef = collection(db, 'drivers');
      // Use ascending order to work with existing index, then sort in JavaScript
      const q = query(
        driversRef, 
        where('companyId', '==', companyId),
        orderBy('driverNumber', 'asc')
      );
      const snapshot = await getDocs(q);
      
      let nextNumber = 1;
      if (!snapshot.empty) {
        // Sort by driverNumber descending in JavaScript to get the highest number
        const sortedDrivers = snapshot.docs
          .map(doc => doc.data())
          .filter(driver => driver.driverNumber)
          .sort((a, b) => (b.driverNumber || 0) - (a.driverNumber || 0));
        
        if (sortedDrivers.length > 0) {
          nextNumber = sortedDrivers[0].driverNumber + 1;
        }
      }
      
      return `DRV${String(nextNumber).padStart(4, '0')}`;
    } catch (error) {
      console.error('Error generating driver code:', error);
      // Fallback to timestamp-based code
      const timestamp = Date.now().toString().slice(-6);
      return `DRV${timestamp}`;
    }
  },

  async getDrivers(companyId) {
    const key = `drivers:${companyId}`;
    const cached = cache.get(key);
    if (cached) return cached;
    const driversRef = collection(db, 'drivers');
    const q = query(driversRef, where('companyId', '==', companyId));
    const snapshot = await getDocs(q);
    const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    cache.set(key, data);
    return data;
  },
  async getDriver(driverId) {
    const driverRef = doc(db, 'drivers', driverId);
    const snapshot = await getDoc(driverRef);
    if (snapshot.exists()) return { id: snapshot.id, ...snapshot.data() };
    return null;
  },
  _invalidate(companyId) { cache.invalidatePrefix('drivers:'); },
  async addDriver(driverData) {
    const driversRef = collection(db, 'drivers');
    
    // Generate driver code and number
    const driverCode = await this.generateDriverCode(driverData.companyId);
    
    // Get the next driver number
    const q = query(
      driversRef, 
      where('companyId', '==', driverData.companyId),
      orderBy('driverNumber', 'asc')
    );
    const snapshot = await getDocs(q);
    
    let driverNumber = 1;
    if (!snapshot.empty) {
      // Sort by driverNumber descending in JavaScript to get the highest number
      const sortedDrivers = snapshot.docs
        .map(doc => doc.data())
        .filter(driver => driver.driverNumber)
        .sort((a, b) => (b.driverNumber || 0) - (a.driverNumber || 0));
      
      if (sortedDrivers.length > 0) {
        driverNumber = sortedDrivers[0].driverNumber + 1;
      }
    }

    const newDriver = {
      ...driverData,
      driverCode,
      driverNumber,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(driversRef, newDriver);
    cache.invalidatePrefix('drivers:');
    auditService.log({
      module: AUDIT_MODULES.DRIVER,
      action: AUDIT_ACTIONS.CREATE,
      recordId: docRef.id,
      recordLabel: driverLabel(newDriver),
      after: newDriver,
      tags: [AUDIT_TAGS.ALL, AUDIT_TAGS.DRIVER_CHANGES],
      companyId: driverData.companyId,
      meta: { driverId: docRef.id }
    });
    return docRef.id;
  },
  async updateDriver(driverId, driverData) {
    const driverRef = doc(db, 'drivers', driverId);
    let before = null;
    try {
      const snap = await getDoc(driverRef);
      if (snap.exists()) before = { id: snap.id, ...snap.data() };
    } catch { /* best effort */ }

    await updateDoc(driverRef, { ...driverData, updatedAt: serverTimestamp() });
    cache.invalidatePrefix('drivers:');

    auditService.logUpdate({
      module: AUDIT_MODULES.DRIVER,
      recordId: driverId,
      recordLabel: driverLabel({ ...before, ...driverData }),
      before,
      after: { ...(before || {}), ...driverData },
      extraTags: [AUDIT_TAGS.DRIVER_CHANGES],
      meta: { driverId }
    });
    return true;
  },
  async deleteDriver(driverId) {
    const driverRef = doc(db, 'drivers', driverId);
    let before = null;
    try {
      const snap = await getDoc(driverRef);
      if (snap.exists()) before = { id: snap.id, ...snap.data() };
    } catch { /* best effort */ }

    await deleteDoc(driverRef);
    cache.invalidatePrefix('drivers:');

    auditService.log({
      module: AUDIT_MODULES.DRIVER,
      action: AUDIT_ACTIONS.DELETE,
      recordId: driverId,
      recordLabel: driverLabel(before),
      before,
      after: null,
      tags: [AUDIT_TAGS.ALL, AUDIT_TAGS.DRIVER_CHANGES, AUDIT_TAGS.DELETIONS],
      companyId: before?.companyId,
      meta: { driverId }
    });
    return true;
  }
}; 